import { Injectable } from '@angular/core';
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  finalize,
  map,
  Observable,
  of,
  shareReplay,
  tap,
} from 'rxjs';
import { MouldDesignDataService } from 'src/app/services';

@Injectable({ providedIn: 'root' })
export class EquipmentLookupService {
  private readonly _equipmentList = new BehaviorSubject<string[]>([]);
  private readonly _selectedEquipmentId = new BehaviorSubject<string | undefined>(undefined);
  private readonly _loading = new BehaviorSubject<boolean>(false);
  private readonly _error = new BehaviorSubject<unknown>(undefined);

  public readonly equipmentList$ = this._equipmentList.asObservable();
  public readonly loading$ = this._loading.asObservable();
  public readonly selectedEquipmentId$ = this._selectedEquipmentId.asObservable();
  public readonly error$ = this._error.asObservable();

  public readonly selectedEquipment$ = combineLatest([
    this.selectedEquipmentId$,
    this.equipmentList$,
  ]).pipe(
    map(([id, list]) => list.find((equipmentId) => equipmentId === id) ?? undefined),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  public constructor(private readonly designService: MouldDesignDataService) {}

  public loadByDesign(designId: string): Observable<string[]> {
    this._loading.next(true);
    this._error.next(undefined);

    return this.fetchEquipmentList(designId).pipe(
      tap((equipmentList) => {
        this._equipmentList.next(equipmentList);
        this._selectedEquipmentId.next(equipmentList[0] ?? undefined);
        this._loading.next(false);
      }),
      catchError((err) => {
        this._error.next(err);
        this._equipmentList.next([]);
        this._selectedEquipmentId.next(undefined);

        return of([]);
      }),
      finalize(() => {
        this._loading.next(false);
      })
    );
  }

  public setSelectedEquipment(equipmentId: string): void {
    this._selectedEquipmentId.next(equipmentId);
    this._error.next(undefined);
  }

  private fetchEquipmentList(designId: string): Observable<string[]> {
    return this.designService.getEquipmentListByDesignId(designId).pipe(
      catchError(() => {
        return of([]);
      })
    );
  }
}
